import * as React from "react";
import { Delete, handleError } from "../../utils/fetch.util";
import { UserContext } from "../../contexts/user.context";
import LoadingData from "../loadingData/loadingData.component";
import { format } from "date-fns";
import ConfirmationModal from "../modals/confirmationModal/confirmationModal.component";
import { t } from "i18next";
import { FiEdit } from "react-icons/fi";

interface VisitorCardProps {
  id: number;
  key: any;
  name: string;
  address: string;
  birthDay: string;
  jobTitle: string;
  reasons: any[];
  duration: number;
  isBlockedForever: boolean;
  blockStartDate: string;
  blockEndDate: string;
  national_id: string;
  getAllVisitorsBlackList: () => any;
  setEditVisitorBlackList: React.Dispatch<any>;
  visitor: any;
  setToggleAddVisitor: React.Dispatch<React.SetStateAction<boolean>>;
}

const VisitorCard: React.FC<VisitorCardProps> = ({
  id,
  key,
  name,
  address,
  birthDay,
  jobTitle,
  reasons,
  duration,
  blockStartDate,
  blockEndDate,
  setEditVisitorBlackList,
  visitor,
  getAllVisitorsBlackList,
  setToggleAddVisitor,
  national_id,
}) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const { getValidToken, permissions } = React.useContext(UserContext);
  const [deleteConfirmationModal, setDeleteConfirmationModal] =
    React.useState<number>(0);

  const handleVisitorBlackList = async () => {
    setIsLoading(true);
    const res = await Delete(
      `visitors-black-list/delete-visitor-blackList?blacklist_id=${id}`,
      getValidToken()
    );

    if (res.error || !res.data) {
      handleError(res);
      setIsLoading(false);
      return;
    }
    getAllVisitorsBlackList();
    setDeleteConfirmationModal(0);
    setIsLoading(false);
  };

  function checkIfIsBlocked(startDateStr: string, endDateStr: string) {
    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);

    const today = new Date();

    for (
      let date = startDate;
      date <= endDate;
      date.setDate(date.getDate() + 1)
    ) {
      if (
        date.toISOString().slice(0, 10) === today.toISOString().slice(0, 10)
      ) {
        return true;
      }
    }

    return false;
  }

  if (isLoading) return <LoadingData />;

  return (
    <>
      <div
        className="border px-4 pt-10 pb-8 rounded min-w-[350px] relative"
        key={key}
      >
        {permissions?.VisitorBlackLists?.update && (
          <div
            className=" absolute top-2 right-2 cursor-pointer"
            onClick={() => {
              setEditVisitorBlackList(visitor);
              setToggleAddVisitor(true);
            }}
          >
            <FiEdit size={22} className="ml-auto mb-3" />
          </div>
        )}
        <div className="flex items-center justify-between mb-1">
          <h5 className="text-[18px] text-gray-500 leading-none mb-1">
            {jobTitle}
          </h5>
          {checkIfIsBlocked(blockStartDate, blockEndDate) ? (
            <span className="bg-red-100 text-red-500 px-3 rounded text-[14px]">
              {t("blocked")}
            </span>
          ) : (
            <span className="bg-orange-100 text-orange-500 px-3 rounded text-[14px]">
              {t("not_blocked")}
            </span>
          )}
        </div>
        <h1 className="text-[25px] font-semibold leading-none mb-3">{name}</h1>
        <span className="block mb-1">
          {t("national_id")}: {national_id}
        </span>
        <span className="block mb-1">
          {t("address")}: {address}
        </span>
        <span className="block mb-1">
          {t("birthday")}: {birthDay.split("T")[0]}
        </span>
        <span className="block mb-1">
          {t("reason")}:{" "}
          {reasons?.map((reason, i) =>
            i == reasons.length - 1 ? reason.reason : reason.reason + " ,"
          )}
        </span>
        <span className="block mb-1">
          {t("duration")}: {duration + " Days"}
        </span>
        <span className="block mb-1">
          {t("block_date")}: {format(new Date(blockStartDate), "yyyy-MM-dd")}
        </span>
        <span className="block">
          {t("block_end_date")}: {format(new Date(blockEndDate), "yyyy-MM-dd")}
        </span>
        {permissions?.VisitorBlackLists?.delete && (
          <button
            className="bg-red-500 text-white rounded-md py-2 px-3 mt-3 w-full"
            onClick={() => setDeleteConfirmationModal(1)}
          >
            {t("remove_from_black_list")}
          </button>
        )}
      </div>
      <ConfirmationModal
        title={t("delete_black_list_visitor")}
        message={t("confirm_delete")}
        isLoading={isLoading}
        onConfirm={() => handleVisitorBlackList()}
        closeConfirmationModal={() => setDeleteConfirmationModal(0)}
        isShown={deleteConfirmationModal > 0}
        strict={true}
      />
    </>
  );
};

export default VisitorCard;
